<script lang="ts" setup>
import { Carousel, CarouselContent, CarouselItem } from '@/components/ui/carousel'
import { useActiveElement } from '@vueuse/core'
import { Loader2 } from 'lucide-vue-next'
const userStore = useUserStore()
const headers = useRequestHeaders(["cookie"]);
const loading = ref(false)
const stripeLoading = ref(false)
const currentSlide = ref(0)
const activeElement = useActiveElement();
const uiStore = useUiStore()
const { setSelectedPlan, setCheckingOut, triggerPrev, triggerNext, setselectedInterval, toggleDialog, setStripeClientSecret, setPlans } = uiStore
const { selectedPlan, checkingOut, prev, next, selectedInterval, plans, stripeClientSecret } = storeToRefs(uiStore)
const { selectedTeam } = storeToRefs(userStore)

// Get current user
const user = useSupabaseUser()

// Get trial configuration
const trialConfig = useTrialConfig()
const { isTrialEnabled, trialPeriodDays, shouldCollectPaymentMethod, trialEndBehavior } = trialConfig

const pro = computed(() => plans.value?.find((plan: any) => plan.name === 'Pro'))
const teams = computed(() => plans.value?.find((plan: any) => plan.name === 'Teams'))

const colorMode = useColorMode()

console.log(selectedPlan.value)
console.log(teams.value)
console.log(pro.value)

const props = withDefaults(defineProps<{
  team?: boolean;
  pro?: boolean;
  planDataFetched?: boolean;
  enableFreeTrial?: boolean;
  trialPeriodDays?: number;
  collectPaymentMethod?: boolean;
  trialEndBehavior?: 'cancel' | 'pause';
}>(), {
  team: false,
  pro: false,
  planDataFetched: false,
  enableFreeTrial: true,
  trialPeriodDays: 30,
  // Removed defaults for collectPaymentMethod and trialEndBehavior
  // so user_trial_settings from database can take precedence
});

watch(activeElement, (el, prevEl) => {
  if (el && el.id !== 'invited-users') {
    el.blur();
  }
});

watch(prev, (val, oldVal) => {
  if (oldVal !== val) {
    scrollPrev();
    currentSlide.value -= 1
  }
})

watch(next, () => {
  scrollNext()
})

onMounted(async () => {
  // Set loading state to show component is working
  loading.value = true;
  
  // Always ensure plans data is available
  if (!plans.value) {
    try {
      const fetchedPlans = await $fetch("/api/plans/list", {
        method: "GET",
        headers,
      });
      setPlans(fetchedPlans?.products)
    } catch (error) {
      console.error("Error loading plans:", error)
    }
  }

  // Wait for plans to be available before setting selected plan
  await nextTick();
  
  // For Basic users, show both Pro and Teams plans
  if (!props.team && !props.pro) {
    // Show both Pro and Teams when user is Basic
    if (pro.value) setSelectedPlan(pro.value)
  } else if (props.pro && pro.value) {
    // When upgrading from Pro, select Pro
    setSelectedPlan(pro.value)
  } else if (props.team && teams.value) {
    // When showing Teams plan, select Teams
    setSelectedPlan(teams.value)
  }
  
  // Defer carousel navigation with a small delay after modal is visible
  setTimeout(() => {
  if (route.query.subscribe) {
    currentSlide.value = 1
    scrollNext()
  }
    // Clear loading state
    loading.value = false;
  }, 300)

  // Also log props on mount for debugging
  console.log('🔍 Props on mount:', {
    collectPaymentMethod: props.collectPaymentMethod,
    collectPaymentMethodType: typeof props.collectPaymentMethod,
    trialEndBehavior: props.trialEndBehavior,
    trialEndBehaviorType: typeof props.trialEndBehavior,
    allProps: { ...props }
  })
});

// At component level, track customer ID
const customerIdCreated = ref('');

// Computed properties for trial logic
const shouldEnableTrial = computed(() => {
  return props.enableFreeTrial ?? isTrialEnabled.value;
});

// Computed properties for effective trial settings (with proper priority handling)
const effectiveCollectPaymentMethod = computed(() => {
  // Check if the prop was actually passed by checking if it's in the component's current instance attrs
  const hasCollectPaymentMethodProp = getCurrentInstance()?.vnode.props?.hasOwnProperty('collectPaymentMethod') || false;
  console.log('🔍 effectiveCollectPaymentMethod check:', {
    hasCollectPaymentMethodProp,
    propsValue: props.collectPaymentMethod,
    shouldCollectFromConfig: shouldCollectPaymentMethod.value,
    willUse: hasCollectPaymentMethodProp ? props.collectPaymentMethod : shouldCollectPaymentMethod.value
  });
  return hasCollectPaymentMethodProp ? props.collectPaymentMethod : shouldCollectPaymentMethod.value;
});

const effectiveTrialEndBehavior = computed(() => {
  const hasTrialEndBehaviorProp = getCurrentInstance()?.vnode.props?.hasOwnProperty('trialEndBehavior') || false;
  return hasTrialEndBehaviorProp ? props.trialEndBehavior : trialEndBehavior.value;
});

// Add debug logging to understand values (after props are initialized)
watch([() => trialConfig.userTrialSettings.value, () => effectiveCollectPaymentMethod.value, () => shouldCollectPaymentMethod.value], 
  ([userSettings, effectiveValue, configValue]) => {
    console.log('🔍 Trial config values:', {
      userSettings,
      propsCollectPaymentMethod: props.collectPaymentMethod,
      propsCollectPaymentMethodType: typeof props.collectPaymentMethod,
      propsCollectPaymentMethodIsUndefined: props.collectPaymentMethod === undefined,
      shouldCollectPaymentMethodFromConfig: configValue,
      effectiveCollectPaymentMethod: effectiveValue,
      propsTrialEndBehavior: props.trialEndBehavior,
      effectiveTrialEndBehavior: effectiveTrialEndBehavior.value
    })
  }, { immediate: true }
)

const subscribe = async () => {
  stripeLoading.value = true
  console.log('SUBSCRIBING!------------------------')
  
  // Wait for user trial settings to be loaded if user is logged in
  if (user.value?.id && trialConfig.userTrialSettings.value === null) {
    console.log('⏳ Waiting for user trial settings to load...')
    await trialConfig.refreshUserSettings()
  }
  
  // Prepare request body with trial options
  const requestBody: any = {
    price_id: selectedPlan.value?.prices[selectedInterval.value].id,
    redirectPath,
    modalOpen: false,
    is_group: selectedPlan.value?.name == 'Teams'
  };

  // Add trial options if free trial is enabled (prioritize props, then config)
  if (shouldEnableTrial.value) {
    requestBody.trial_period_days = props.trialPeriodDays ?? trialPeriodDays.value;
    requestBody.collect_payment_method = effectiveCollectPaymentMethod.value;
    requestBody.trial_end_behavior = effectiveTrialEndBehavior.value;
    
    console.log(`🆓 Starting ${requestBody.trial_period_days}-day free trial ${!requestBody.collect_payment_method ? 'without payment method collection' : 'with payment method collection'}`);
    console.log(`📊 Trial config source - User settings: ${JSON.stringify(trialConfig.userTrialSettings.value)}, Props: collect_payment_method=${props.collectPaymentMethod}, trial_end_behavior=${props.trialEndBehavior}`);
  }

  const data: any = await $fetch('/api/subscribe', {
    method: 'POST',
    headers,
    body: requestBody
  })
  stripeLoading.value = false
  
  if (data) {
  // Save the customer ID to clean up if needed
    if (data.customer) {
    customerIdCreated.value = data.customer;
    }
    
    // Handle embedded checkout
    if (data.embedded && data.clientSecret) {
      setStripeClientSecret(data.clientSecret)
    }
    // Handle hosted checkout (redirect)
    else if (data.url) {
      window.location.href = data.url;
    }
    // Legacy support for old response format
    else if (data.message && !stripeClientSecret.value) {
      setStripeClientSecret(data.message)
    }
  }
  return
}

onBeforeUnmount(() => {
  // If we have created a checkout session but haven't completed it, notify server
  if (customerIdCreated.value && !selectedTeam.value?.subscription_status) {
    // Call a cleanup endpoint to mark this customer as abandoned
    $fetch('/api/subscribe/cancel', {
      method: 'POST',
      headers,
      body: {
        customer_id: customerIdCreated.value,
      }
    }).catch(err => {
      console.error('Failed to clean up checkout session:', err)
    })
  }
})

const carouselContainerRef = ref<InstanceType<typeof Carousel> | null>(null)
const carouselContainer2Ref = ref<InstanceType<typeof Carousel> | null>(null)

const api = ref<CarouselApi>()
const api2 = ref<CarouselApi>()

const setApi = (val: CarouselApi) => {
  api.value = val
}

const setApi2 = (val: CarouselApi) => {
  api2.value = val
}


const stop = watch(api, (api) => {
  if (!api)
  return

  // Watch only once or use watchOnce() in @vueuse/core
  nextTick(() => stop())

  api.on('settle', (e, p) => {
    // currentSlide.value = Math.max(0, Math.round(e.scrollProgress()))
    })
})

const emblaOpts = ref({
  active: true,
  loop: false,
  duration: 15,
  align: 'start'
})

const scrollNext = () => {
  carouselContainerRef.value?.carouselApi.scrollNext()
  carouselContainer2Ref.value?.carouselApi.scrollNext()
  currentSlide.value+=1
}

const scrollPrev = () => {
  
  setCheckingOut(false)
  carouselContainerRef.value?.carouselApi.scrollPrev()
  carouselContainer2Ref.value?.carouselApi.scrollPrev()
  currentSlide.value-=1
}

const route = useRoute()
const redirectPath = route.path

const clientSecret = ref('')
const stripeLoaded = ref(false)

const upgradeMessage = computed(() => {
  if (props.team) return 'Upgrade to Teams for advanced collaboration';
  if (props.pro) return 'Upgrade to Pro for more features';
  return 'Try SuperSeeded for free';
});

</script>

<template>
  <div class="w-full max-h-[90vh] transition-height duration-300">
    <!-- <Button class="pointer-events-auto" @click="async ()=>{await toggleDialog(false); console.log('clsing')}">Close</Button> -->
    <div class="hidden md:block inset-0 absolute w-full flex justify-start items-start -top-10 pl-10 pointer-events-none">
      <div class="max-w-[30vw] blur-sm">
        <!-- <LiquidLogo image-url="https://ik.imagekit.io/8qxqs9cznn/general/logos/superseeded_logo_small.png" color1="#FBCA4F" color2="#FB5FDC"  :chromaticStrength="2.5" :refraction="0.12" /> -->
      </div>
    </div>
    
    <!-- Loading state -->
    <div v-if="loading && (!plans || !pro || !teams)" class="w-full h-[50vh] flex justify-center items-center">
      <div class="flex flex-col items-center">
        <Loader2 class="h-8 w-8 animate-spin text-primary mb-4" />
        <p class="text-center text-muted-foreground">Loading plan options...</p>
      </div>
    </div>

    <!-- Main content when loaded -->
    <div v-else
      :class="[`w-full h-fit md:h-fit md:w-[90vw] max-h-[95vh] grid-rows-2 grid-cols-1 md:grid-rows-1 grid md:grid-cols-2 grid-flow-col auto-cols-auto auto-rows-auto rounded-lg items-center p-0 flex`, { 'overflow-y-auto': stripeClientSecret ? true : false }]">
      


      <Carousel v-if="!checkingOut && (teams || !props.pro)" @init-api="setApi" ref="carouselContainerRef" :opts="emblaOpts"
        :class="['w-full px-6 pointer-events-none order-2 md:order-1']">
        <CarouselContent class="items-end md:items-start">
          <CarouselItem class="flex justify-center sm:p-8">
            <div>
              <Badge
                class="bg-muted text-primary outline-muted-foreground/40 outline outline-[1px] text-xs font-normal max-w-[90%] ml-4 absolute top-2 md:top-16 z-10">
                <div class="truncate">

                  <p class="hidden sm:inline truncate">
                    Landscape architects, consultants
                    & nurseries
                  </p>
                  <p class="inline-block sm:hidden truncate">
                    LA, consultant
                    & nursery
                  </p>
                </div>
              </Badge>
            </div>
            <PlanCard 
              v-if="teams" 
              :plan="teams"
              :features="[
                { 
                  text: 'Share planting assets with your team', 
                  icon: 'ic:twotone-done' 
                }, 
                { 
                  text: 'Unlimited teams, members and projects', 
                  icon: 'ic:twotone-done' 
                }, 
                { 
                  text: 'Unlimited private projects',
                  icon: 'ic:twotone-done' 
                }, 
                { 
                  text: 'Dedicated support team',
                  icon: 'ic:twotone-done' 
                },
                { 
                  text: 'Upload supplier catalogues',
                  icon: 'ic:twotone-done' 
                }
                
                
              ]" 
            />
          </CarouselItem>
          <CarouselItem key="page3" class="sm:p-8">
            <div class="max-w-[400px] dark:bg-background/30 bg-primary/5 squircle-12 rounded-lg backdrop-blur-md p-4" v-if="selectedPlan">
              <p class="text-3xl font-[800] py-4">{{ upgradeMessage }}</p>
              <small class="text-sm font-medium leading-none">For a team of one person:</small>

              <RadioGroup v-if="plans && selectedPlan" v-model="selectedInterval" default-value="year"
                class="space-y-6 mt-4">
                <div class="flex items-center space-x-6 w-fit pointer-events-auto cursor-pointer"
                  @dragstart.stop.prevent>
                  <RadioGroupItem id="r1" value="year" />
                  <Label for="r1">
                    <p class="mb-1 text-xl font-semibold tracking-tight">
                      Yearly<Badge class="bg-destructive relative -top-0.5 text-background text-xs font-semibold mx-2 ">
                        Save ${{ ((selectedPlan.prices.month.unit_amount * 12 - selectedPlan.prices.year.unit_amount) / 100).toFixed(0) }}</Badge>
                    </p>
                    <small class="text-sm font-medium text-muted-foreground leading-none">
                      <span>
                        AUD ${{ selectedPlan.prices.year.unit_amount / 100 }}
                      </span>
                      <span class="text-muted-foreground pl-1 font-light">
                        (AUD ${{ (selectedPlan.prices.year.unit_amount / 100 / 12).toFixed(2) }} / month)
                      </span>
                    </small>
                  </Label>
                </div>
                <div class="flex items-center w-fit space-x-6 pointer-events-auto cursor-pointer"
                  @dragstart.stop.prevent>
                  <RadioGroupItem id="r2" value="month" />
                  <Label for="r2">
                    <p class="mb-1 text-xl font-semibold tracking-tight">
                      Monthly
                    </p>
                    <small class="text-sm font-medium text-muted-foreground leading-none">
                      AUD ${{ selectedPlan.prices.month.unit_amount / 100 }}
                    </small>
                  </Label>
                </div>

              </RadioGroup>
              <div class="text-sm tracking-tight text-primary pt-12">
                <div v-if="shouldEnableTrial" class="w-full flex justify-between pb-2">
                  <div>
                    Due {{ new Date(Date.now() + (props.trialPeriodDays ?? trialPeriodDays.value) * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { month: 'long',
                    day: 'numeric', year: 'numeric' }) }}
                  </div>
                  <div>
                    {{ selectedInterval === 'year' ? 'AUD $' +
                    (selectedPlan.prices[selectedInterval].unit_amount/100).toFixed(2) :
                    'AUD $' + (selectedPlan.prices[selectedInterval].unit_amount/100).toFixed(2) }}
                  </div>
                </div>
                <div class="w-full flex justify-between font-semibold">
                  <div>
                    {{ shouldEnableTrial ? `Due today (${props.trialPeriodDays ?? trialPeriodDays.value} days free${!effectiveCollectPaymentMethod ? ', no payment method required' : ''})` : 'Due today' }}
                  </div>
                  <div>
                    {{ shouldEnableTrial ? 'AUD $0' : 'AUD $' + (selectedPlan.prices[selectedInterval].unit_amount/100).toFixed(2) }}
                  </div>
                </div>
                <div v-if="shouldEnableTrial && !effectiveCollectPaymentMethod" class="text-xs text-muted-foreground mt-2">
                  You can add a payment method later to continue after your trial ends.
                </div>
              </div>
              <Button @click="subscribe(); setCheckingOut(true)" class="w-full pointer-events-auto mt-8 text-lg">
                Next
              </Button>
            </div>
          </CarouselItem>



        </CarouselContent>
      </Carousel>
      <div v-if="!checkingOut" class="hidden md:block absolute inset-0 m-0 p-0 pointer-events-none h-full z-0 left-0 right-1/2 w-1/2 rounded-l-2xl">
        <NuxtImg 
          :src="colorMode.preference === 'dark' ? 'https://ik.imagekit.io/8qxqs9cznn/general/checkout_dark.jpg' : 'general/checkout.jpg'" 
          class="w-full h-full object-cover rounded-l-2xl" 
        />
      </div>
      <Carousel v-if="!checkingOut && !props.team" class="w-full p-6 md:p-0 sm:px-2 pointer-events-none order-1 md:order-2 md:px-4"
        @init-api="setApi2" :opts="emblaOpts" ref="carouselContainer2Ref">
        <CarouselContent class="items-center">
          <CarouselItem key="page2" class="flex justify-center md:justify-start sm:p-8">
            <PlanCard 
              v-if="pro" 
              :plan="pro"
              :features="[
                { 
                  text: '100% of your schedule analysed', 
                  icon: 'ic:twotone-done' 
                }, 
                { 
                  text: '5 private projects', 
                  icon: 'ic:twotone-done' 
                }, 
                { 
                  text: 'Per-schedule life-cycle costing', 
                  icon: 'ic:twotone-done' 
                }
              ]" 
            />
          </CarouselItem>
          <CarouselItem key="page3" class="md:px-6 lg:px-14 ">
            <PlanTimeline class="" :stripeLoaded="true" />
          </CarouselItem>
        </CarouselContent>
      </Carousel>
      <!-- <div class="h-full flex relative flex-col -order-1 md:order-1 w-full md:flex-row md:space-x-4 lg:justify-between m-0 p-0
      bg-center bg-cover bg-no-repeat bg-[url('/euc1.jpg')] h-full rounded-lg rounded-b-none md:rounded-l-none
      md:rounded-r-lg"></div> -->

    </div>

    <div v-if="checkingOut" class="w-full min-h-[50vh] h-fit flex justify-center align-center p-[1.85rem] -mt-[4rem] py-24">
      <PlanConfirm :loading="stripeLoading" :plan="selectedPlan" @loaded="stripeLoaded = true" />

    </div>
    <div v-else>

      <!-- <div class="flex justify-center align-center">
        <div class="absolute">
          <Loader2 class="w-8 h-8 my-4 animate-spin" /> 
        </div>
      </div> -->
    </div>
  </div>
</template>

<style scoped>
.carousel,
.carousel-content,
.carousel-item {
  height: 100%;
}

.embla__slide {
  margin-right: 0px;
  /* Default slide gap */
}

@media (min-width: 768px) {
  .embla__slide {
    margin-right: 0px;
    /* Breakpoint SM gap */
  }
}
</style>